import React from 'react';
import { withFormsy } from 'formsy-react';
import { MenuItem, Typography } from '@mui/material';

import BaseTextField, { type FieldProps } from './components/BaseTextField';

export interface SelectFieldOptionType {
	value: string | number;
	title: string;
	description?: string;
}

export type SelectFieldProps = {
	/** The options list */
	options: SelectFieldOptionType[];
	/** If true, disable the empty option */
	disableEmptyOption?: boolean;
} & Omit<FieldProps<string | number>, 'startAdornment' | 'endAdornment'>;

/** Field used to provide selection capabilities from a list of items */
function SelectField(props: SelectFieldProps) {
	function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
		const newValue = event.target.value === '' ? null : event.target.value;
		if (props.beforeChange) props.beforeChange(newValue ?? undefined);
		props.setValue(newValue);
		props.sendChange(props.name, newValue);
		if (props.afterChange) props.afterChange(newValue ?? undefined);
	}

	return (
		<BaseTextField
			className={props.className}
			name={props.name}
			select
			value={props.value || ''}
			label={props.label}
			isEdit={props.isEdit}
			required={props.required}
			errorMessage={props.errorMessage}
			isFormSubmitted={props.isFormSubmitted}
			isPristine={props.isPristine}
			isValid={props.isValid}
			logProps={props.logProps}
			onChange={handleChange}
		>
			{!(props.required || props.disableEmptyOption) && <MenuItem value="">-</MenuItem>}
			{props.options.map(option => (
				<MenuItem key={option.value} className="flex flex-col items-start" value={option.value}>
					<Typography className="font-400" variant="subtitle2" color="text.primary">
						{option.title}
					</Typography>
					{option.description && (
						<Typography className="font-400" variant="subtitle2" color="text.secondary">
							{option.description}
						</Typography>
					)}
				</MenuItem>
			))}
		</BaseTextField>
	);
}

export default withFormsy(React.memo(SelectField));
